/**
 * Snagcart platform tutorial — modules, workflows, and navigation help.
 * Used by PlatformGuide page and PlatformHelpWidget.
 */

export const TUTORIAL_CATEGORIES = [
  { id: 'start', label: 'Getting Started', color: 'text-emerald-400' },
  { id: 'build', label: 'Build & Publish', color: 'text-violet-400' },
  { id: 'ai', label: 'AI & Content', color: 'text-blue-400' },
  { id: 'grow', label: 'Marketing & Growth', color: 'text-pink-400' },
  { id: 'operate', label: 'Operate & Automate', color: 'text-amber-400' },
  { id: 'integrations', label: 'API & Integrations', color: 'text-sky-400' },
  { id: 'account', label: 'Plans & Account', color: 'text-cyan-400' },
];

/** Where to configure keys in the product UI */
export const SETTINGS_API_KEYS_PATH = '/settings?tab=api-keys';

/**
 * API setup knowledge base — maps Settings → API Keys fields to platform modules.
 * Keep in sync with backend API_KEY_FIELDS in server.py.
 */
export const API_SETUP_BLOCKS = [
  {
    id: 'ai',
    label: 'AI & LLM',
    color: 'from-violet-500 to-purple-500',
    modules: ['AI Tools', 'Snag Builder', 'AI Studio', 'Media Studio', 'AI Co-founder (roadmap & drafts)'],
    keys: [
      { key: 'OPENAI_API_KEY', label: 'OpenAI API Key', required: true, usedFor: 'Default LLM for AI Writer, Chat, Snag Builder, and most AI Tools.' },
      { key: 'ANTHROPIC_API_KEY', label: 'Anthropic API Key', usedFor: 'Claude models in Snag Builder and advanced agents.' },
      { key: 'GOOGLE_API_KEY', label: 'Google AI / Gemini Key', usedFor: 'Gemini models and Nano Banana image generation in AI Tools.' },
      { key: 'MUAPI_KEY', label: 'MuAPI Key', usedFor: 'Media Studio image/video/audio — optional; shared key used if blank.' },
      { key: 'AZURE_OPENAI_API_KEY', label: 'Azure OpenAI', usedFor: 'Enterprise Azure-hosted OpenAI (also set endpoint + API version).' },
      { key: 'OLLAMA_BASE_URL', label: 'Ollama Base URL', usedFor: 'Run local models (e.g. http://localhost:11434) instead of cloud APIs.' },
    ],
    setup: [
      'Go to Settings → API Keys → AI & LLM.',
      'Paste OPENAI_API_KEY first — most modules need this to work.',
      'Click Test connection where available after saving.',
      'Add alternate providers (Anthropic, Google) if you switch models in Snag Builder or AI Tools.',
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    color: 'from-green-500 to-emerald-500',
    modules: ['Site eCommerce checkout', 'Subscription plans (live mode)', 'Operator payments'],
    keys: [
      { key: 'STRIPE_API_KEY', label: 'Stripe Secret Key', required: true, usedFor: 'Platform admin: users pay for plans/credits via this Stripe account (sk_live_ or sk_test_).' },
      { key: 'STRIPE_WEBHOOK_SECRET', label: 'Stripe Webhook Secret', required: true, usedFor: 'Verify /api/webhook/stripe for plan and credit purchases.' },
      { key: 'PHONEPE_MERCHANT_ID', label: 'PhonePe Merchant ID', required: false, usedFor: 'India plan checkout (platform admin).' },
      { key: 'PHONEPE_SALT_KEY', label: 'PhonePe Salt Key', required: false, usedFor: 'PhonePe API secret for India plan checkout.' },
    ],
    setup: [
      'As platform admin, open Settings → API Keys → Platform payments.',
      'Paste Stripe Secret Key + Webhook Secret (endpoint: /api/webhook/stripe).',
      'For India, add PhonePe Merchant ID, Salt Key, Salt Index, and Env (UAT or PRODUCTION).',
      'Optional: Stripe Price IDs + Subscription Mode=true for recurring billing.',
      'Use SUBSCRIPTION_BILLING_MODE=mock locally without live gateways.',
    ],
  },
  {
    id: 'email',
    label: 'Email delivery',
    color: 'from-pink-500 to-rose-500',
    modules: ['Email Marketing', 'Automations', 'Support notifications', 'Lead pipeline enrichment', 'AI Co-founder (send campaigns)'],
    keys: [
      { key: 'SENDGRID_API_KEY', label: 'SendGrid API Key', usedFor: 'Primary email marketing and campaign delivery.' },
      { key: 'RESEND_API_KEY', label: 'Resend API Key', usedFor: 'Alternative transactional email provider.' },
      { key: 'MAILGUN_DOMAIN', label: 'Mailgun Domain', usedFor: 'Mailgun sending — pair with MAILGUN_API_KEY.' },
      { key: 'MAILGUN_API_KEY', label: 'Mailgun API Key', usedFor: 'Mailgun email delivery.' },
      { key: 'SMTP_HOST', label: 'SMTP Host / Port / User / Password', usedFor: 'Generic SMTP (Gmail app password, Office 365, etc.). Set SMTP_FROM_EMAIL too.' },
    ],
    setup: [
      'Pick one delivery method: SendGrid, Resend, Mailgun, or SMTP.',
      'Settings → API Keys → Email — save keys and use Test connection.',
      'Settings → Support notifications tab for ticket email separately if needed.',
      'Lead enrichment (email + contacts) is included with platform credits on paid plans.',
    ],
  },
  {
    id: 'communications',
    label: 'SMS, WhatsApp & voice',
    color: 'from-blue-500 to-cyan-500',
    modules: ['SMS Marketing', 'WhatsApp Marketing', 'Voice Campaigns', 'Bot Leads & Calls'],
    keys: [
      { key: 'TWILIO_ACCOUNT_SID', label: 'Twilio Account SID', usedFor: 'SMS campaigns, voice outbound, and call bots.' },
      { key: 'TWILIO_AUTH_TOKEN', label: 'Twilio Auth Token', usedFor: 'Twilio authentication — test with Test connection.' },
      { key: 'TWILIO_PHONE_NUMBER', label: 'Twilio Phone Number', usedFor: 'Caller ID for SMS and voice.' },
      { key: 'TEXTLOCAL_API_KEY', label: 'Textlocal API Key', usedFor: 'Alternative SMS provider (India/UK).' },
      { key: 'EVOLUTION_API_URL', label: 'Evolution API URL', usedFor: 'Self-hosted WhatsApp bridge (e.g. http://localhost:8080).' },
      { key: 'EVOLUTION_API_KEY', label: 'Evolution API Key', usedFor: 'Auth for Evolution WhatsApp instances.' },
      { key: 'ELEVENLABS_API_KEY', label: 'ElevenLabs API Key', usedFor: 'AI voice synthesis for Voice Campaigns.' },
    ],
    setup: [
      'Twilio: console.twilio.com → Account SID, Auth Token, buy a phone number.',
      'WhatsApp: deploy Evolution API or use Connections for OAuth where supported.',
      'Save all keys under Settings → API Keys → Communications / Voice AI.',
      'Voice Campaigns and Bot Leads add-ons require Twilio + plan/add-on activation.',
    ],
  },
  {
    id: 'google',
    label: 'Google services',
    color: 'from-red-500 to-orange-500',
    modules: ['Lead Scraper', 'Connections (OAuth)', 'Appointments', 'AI Plagiarism Checker'],
    keys: [
      { key: 'GOOGLE_MAPS_API_KEY', label: 'Google Maps / Places API Key', required: true, usedFor: 'Lead Scraper business search — enable Places API in Google Cloud.' },
      { key: 'GOOGLE_CLIENT_ID', label: 'Google OAuth Client ID', usedFor: 'First-party Google login and Calendar sync via Connections.' },
      { key: 'GOOGLE_CLIENT_SECRET', label: 'Google OAuth Client Secret', usedFor: 'OAuth token exchange for Google login and Calendar.' },
      { key: 'GOOGLE_LOGIN_REDIRECT_URI', label: 'Google Login Redirect URI', usedFor: 'Optional override for /api/auth/google/callback (register in Google Cloud Console).' },
      { key: 'GOOGLE_CALENDAR_OWNER_EMAIL', label: 'Calendar owner email', usedFor: 'AI booking agent and appointment modules.' },
      { key: 'GOOGLE_CSE_API_KEY', label: 'Custom Search API Key', usedFor: 'Plagiarism checker web search.' },
      { key: 'GOOGLE_CSE_CX', label: 'Custom Search Engine ID', usedFor: 'Search engine ID for plagiarism detection.' },
    ],
    setup: [
      'Google Cloud Console → create project → enable Maps Places API for Lead Scraper.',
      'OAuth credentials: APIs & Services → Credentials → OAuth client for Connections.',
      'Restrict API keys by HTTP referrer or IP in production.',
    ],
  },
  {
    id: 'domains',
    label: 'Domain registration',
    color: 'from-amber-500 to-yellow-500',
    modules: ['Domains & Hosting', 'DNS automation'],
    keys: [
      { key: 'REGISTRAR_PROVIDER', label: 'Registrar provider', usedFor: 'Set to namesilo, godaddy, namecheap, or mock for dev.' },
      { key: 'NAMESILO_API_KEY', label: 'NameSilo API Key', usedFor: 'Live domain register/renew — Account → API Manager.' },
      { key: 'GODADDY_API_KEY', label: 'GoDaddy API Key + Secret', usedFor: 'GoDaddy domain portfolio.' },
      { key: 'NAMECHEAP_API_USER', label: 'Namecheap API User + Key', usedFor: 'Namecheap domain management.' },
    ],
    setup: [
      'Settings → API Keys → Domain Registration.',
      'Set REGISTRAR_PROVIDER to match your registrar.',
      'Use Test connection for NameSilo after saving.',
      'Mock registrar works without keys for local development.',
    ],
  },
  {
    id: 'social',
    label: 'Social media publishing',
    color: 'from-sky-500 to-blue-600',
    modules: ['AI Social Media', 'Marketing Suite social posts', 'AI Co-founder (publish)'],
    keys: [
      { key: 'TWITTER_API_KEY', label: 'Twitter/X API keys (4 fields)', usedFor: 'Post to X — developer.twitter.com project keys + access tokens.' },
      { key: 'LINKEDIN_ACCESS_TOKEN', label: 'LinkedIn token + Person URN', usedFor: 'Publish to LinkedIn profile or company page.' },
      { key: 'FACEBOOK_PAGE_ID', label: 'Facebook Page ID + Access Token', usedFor: 'Facebook Page posts via Graph API. Required for AI Co-founder live Facebook posts.' },
      { key: 'INSTAGRAM_BUSINESS_ID', label: 'Instagram Business ID + Token', usedFor: 'Instagram content publish (linked to Facebook Page).' },
      { key: 'YOUTUBE_CHANNEL_ID', label: 'YouTube Channel ID', usedFor: 'YouTube metadata and AI YouTube tool.' },
    ],
    setup: [
      'Each network has its own developer portal — gather all tokens before saving.',
      'Settings → API Keys → Social Media Publishing.',
      'Use Test connection for Twitter, LinkedIn, and Facebook where shown.',
      'AI Co-founder can draft posts without these keys; Approve → publish needs them.',
    ],
  },
  {
    id: 'booking',
    label: 'Booking',
    color: 'from-teal-500 to-cyan-500',
    modules: ['Site Appointments', 'AI booking agent'],
    keys: [
      { key: 'CALCOM_API_KEY', label: 'Cal.com API Key', usedFor: 'Sync availability and create bookings via Cal.com.' },
    ],
    setup: [
      'Cal.com → Settings → Developer → API keys.',
      'Save under Settings → API Keys → Booking.',
    ],
  },
];

/** Which modules need which API categories configured */
export const API_MODULE_MATRIX = [
  { module: 'AI Tools / Snag Builder', categories: ['ai'], minKeys: 'OPENAI_API_KEY' },
  { module: 'Media Studio', categories: ['ai'], minKeys: 'OPENAI_API_KEY or MUAPI_KEY' },
  { module: 'Email Marketing', categories: ['email'], minKeys: 'SENDGRID_API_KEY or SMTP_*' },
  { module: 'SMS Marketing', categories: ['communications'], minKeys: 'TWILIO_*' },
  { module: 'WhatsApp Marketing', categories: ['communications'], minKeys: 'EVOLUTION_API_*' },
  { module: 'Voice Campaigns', categories: ['communications'], minKeys: 'TWILIO_* + ELEVENLABS_API_KEY' },
  { module: 'Lead Scraper', categories: ['google', 'email'], minKeys: 'GOOGLE_MAPS_API_KEY' },
  { module: 'Domains & Hosting', categories: [], minKeys: 'Platform NAMESILO_API_KEY in backend env' },
  { module: 'Site eCommerce', categories: ['payments'], minKeys: 'STRIPE_API_KEY' },
  { module: 'AI Social Media', categories: ['social', 'ai'], minKeys: 'Platform tokens + OPENAI_API_KEY' },
  { module: 'AI Co-founder', categories: ['ai', 'email', 'social'], minKeys: 'OPENAI_API_KEY (drafts); email/social keys only for live send/publish' },
  { module: 'Connections (OAuth)', categories: ['google'], minKeys: 'Use Connections page — some keys also in Settings' },
];

export const QUICK_WORKFLOWS = [
  {
    id: 'first-site',
    title: 'Launch your first website',
    duration: '10 min',
    category: 'start',
    path: '/dashboard',
    steps: [
      'Open My Websites and click Create or pick a template.',
      'Name your site and choose a template or AI-generated layout.',
      'Edit pages in the template editor — text, images, colors.',
      'Click Publish to go live on your Snagcart subdomain.',
      'Optional: attach a custom domain under Domains & Hosting.',
    ],
  },
  {
    id: 'marketing-campaign',
    title: 'Run an email campaign',
    duration: '15 min',
    category: 'grow',
    path: '/marketing-suite/email',
    steps: [
      'Go to Marketing Suite → Email.',
      'Import or add contacts to a list.',
      'Create a campaign with subject, body, and template.',
      'Send a test email, then schedule or send now.',
      'Review opens and clicks in campaign analytics.',
    ],
  },
  {
    id: 'ai-content',
    title: 'Create content with AI Tools',
    duration: '5 min',
    category: 'ai',
    path: '/ai-tools-dashboard',
    steps: [
      'Open AI Tools from the sidebar.',
      'Pick AI Writer for blogs, AI Chat for Q&A, or AI Editor to polish copy.',
      'Enter your prompt and generate — copy into your website or campaign.',
      'Use Media Studio for images and AI Presentation for slide decks.',
    ],
  },
  {
    id: 'ai-cofounder-start',
    title: 'Run the AI Co-founder',
    duration: '10 min',
    category: 'ai',
    path: '/ai-cofounder',
    steps: [
      'Optional but recommended: Settings → API Keys → add OPENAI_API_KEY (smarter roadmaps).',
      'For live Facebook/email later: add Social keys and email provider (or connect Gmail in Connections).',
      'Open AI Co-founder → enter company name, what it does, goals, audience → Create.',
      'Click Launch co-founder — it builds a roadmap and starts safe work (CRM, drafts, Builder).',
      'Watch Live activity. When something needs send/publish, Approve or Reject.',
      'Open Outputs hub to see every result with links into CRM, Email, Builder, SEO, Ads.',
      'Pause anytime to save the session; reopen past runs under Sessions.',
    ],
  },
  {
    id: 'configure-apis',
    title: 'Configure platform API keys',
    duration: '15 min',
    category: 'integrations',
    path: '/settings?tab=api-keys',
    steps: [
      'Open Settings → API Keys (or Platform Guide → API Setup).',
      'Add OPENAI_API_KEY first — required for AI Tools and Snag Builder.',
      'Add email (SendGrid or SMTP) before running Email Marketing campaigns.',
      'Add GOOGLE_MAPS_API_KEY before using Lead Scraper.',
      'Use Test connection buttons after each category; keys apply platform-wide.',
    ],
  },
  {
    id: 'upgrade-plan',
    title: 'Choose a plan & buy add-ons',
    duration: '5 min',
    category: 'account',
    path: '/subscription-plans',
    steps: [
      'Open Choose Plan to compare Basic, Starter, and Growth.',
      'Select a paid plan and pay with Stripe (card) or PhonePe (India).',
      'View activated modules under Snagcart Modules.',
      'Optional extra AI tools can be bought from Snagcart Modules.',
    ],
  },
  {
    id: 'app-builder',
    title: 'Build a full-stack app',
    duration: '30+ min',
    category: 'build',
    path: '/app-builder',
    steps: [
      'Open Snag Builder — included on Basic, Starter, and Growth.',
      'Describe your app in chat — the agent plans and generates code.',
      'Review files in the IDE panel and preview in the browser pane.',
      'Iterate with follow-up prompts; deploy when validation passes.',
    ],
  },
  {
    id: 'publish-vercel',
    title: 'Publish app — deploy with Vercel',
    duration: '15 min',
    category: 'build',
    path: '/platform-guide?section=publish-vercel',
    steps: [
      'Snag Builder → open an app → Publish tab.',
      'Sync to GitHub first.',
      'Create a Vercel token with Full Account (or Owner team) scope and paste it in Settings → Vercel.',
      'On vercel.com/account, add GitHub under Login Connections and authorize the Vercel GitHub App.',
      'Create Project (or Connect existing) and open the live URL.',
    ],
  },
  {
    id: 'lead-pipeline',
    title: 'Find leads and follow up',
    duration: '20 min',
    category: 'grow',
    path: '/lead-scraper',
    steps: [
      'Use Lead Scraper to search businesses by niche and location.',
      'Export leads to CSV or push to Marketing CRM.',
      'Create an automation: new lead → welcome email or SMS.',
      'Optional: Bot Leads & Calls add-on for AI inbound handling.',
    ],
  },
];

export const TUTORIAL_SECTIONS = [
  {
    id: 'welcome',
    category: 'start',
    title: 'Welcome to Snagcart',
    summary: 'Your all-in-one platform to build websites, run marketing, and automate growth with AI.',
    path: '/dashboard',
    pathLabel: 'Open Dashboard',
    routeMatch: ['/dashboard', '/', '/platform-guide'],
    keywords: ['welcome', 'start', 'overview', 'home', 'navigate', 'navigation'],
    steps: [
      { title: 'Sidebar navigation', body: 'Every major product lives in the left sidebar: websites, AI Co-founder, AI tools, marketing, domains, and more. Click any item to open that module.' },
      { title: 'My Websites is home base', body: 'The dashboard lists all your sites and quick actions to create from templates or AI.' },
      { title: 'AI Co-founder for autopilot', body: 'Want the platform to run growth work for you? Open AI Co-founder — it plans tasks and writes into CRM, email, Builder, SEO, and social (you approve sends/publishes).' },
      { title: 'Contextual help', body: 'Click the ? button (bottom-right) anytime for tips about the page you are on. Open Platform Guide for the full manual.' },
      { title: 'Plans set limits, not modules', body: 'Basic, Starter, and Growth all include every dashboard module. You choose sites, credits, and caps. Optional extra AI tools are on Snagcart Modules.' },
    ],
    tips: ['Use the search bar on My Websites to filter many projects.', 'Settings (bottom of sidebar) holds profile, API keys, and integrations.'],
    related: ['ai-cofounder', 'websites', 'subscription-plans', 'snagcart-modules'],
  },
  {
    id: 'websites',
    category: 'build',
    title: 'My Websites',
    summary: 'Create, edit, publish, and manage all your sites from one place.',
    path: '/dashboard',
    pathLabel: 'My Websites',
    routeMatch: ['/dashboard', '/edit/', '/editor/', '/builder/', '/preview/', '/generate'],
    keywords: ['website', 'site', 'create', 'template', 'publish', 'edit'],
    steps: [
      { title: 'Create a site', body: 'Click Create or use quick actions: templates gallery, AI generator, or Snagcart Builder for full apps.' },
      { title: 'Edit content', body: 'Open a site → Edit opens the template editor. Change text, sections, images, and SEO settings.' },
      { title: 'Visual builder', body: 'Some sites support the visual builder for drag-and-drop layout changes.' },
      { title: 'Publish', body: 'Publish makes the site live at yourname.snagcart.app (or your custom domain after DNS setup).' },
      { title: 'Per-site tools', body: 'From a site card: Admin, eCommerce, CRM, Bookings, and Marketing panels are scoped to that website.' },
    ],
    tips: ['Published URL and custom domain status show on each site card.', 'Website Settings control favicon, analytics, and login gates.'],
    related: ['domains', 'ecommerce', 'app-builder'],
  },
  {
    id: 'domains',
    category: 'build',
    title: 'Domains & Hosting',
    summary: 'Register domains, attach them to websites, and automate DNS.',
    path: '/domains-hosting',
    pathLabel: 'Domains',
    routeMatch: ['/domains-hosting'],
    keywords: ['domain', 'dns', 'hosting', 'custom domain', 'ssl', 'attach'],
    steps: [
      { title: 'Domain portfolio', body: 'View registered domains and their connection status to websites.' },
      { title: 'Attach to a site', body: 'Link a domain to a published website — Snagcart updates routing when DNS is correct.' },
      { title: 'DNS checklist', body: 'Follow the operations checklist: A/CNAME records, verification, and SSL provisioning.' },
      { title: 'Publish first', body: 'The site must be published before a custom domain will resolve correctly.' },
    ],
    tips: ['Starter plan and above include custom domain attach.', 'DNS changes can take up to 48 hours to propagate globally.'],
    related: ['websites', 'subscription-plans'],
  },
  {
    id: 'snag-builder',
    category: 'build',
    title: 'Snag Builder',
    summary: 'AI website & app builder — describe a site, generate the stack, preview, and deploy. Included on Basic, Starter, and Growth.',
    path: '/snagbuilder',
    pathLabel: 'Snag Builder',
    routeMatch: ['/snagbuilder', '/alifullstack', '/app-builder', '/gpt-pilot'],
    keywords: ['snag builder', 'full stack', 'deploy', 'code', 'sitebrain', 'ai website'],
    steps: [
      { title: 'Describe your app', body: 'Tell the agent what to build — site, CRUD app, dashboard, SaaS MVP, etc.' },
      { title: 'Phased pipeline', body: 'Watch phases stream: plan → architect → implement → validate → preview.' },
      { title: 'IDE & preview', body: 'Browse generated files, edit code, and see live preview in the split pane.' },
      { title: 'Follow-up chat', body: 'Ask for changes; the agent applies updates autonomously by default.' },
      { title: 'Deploy & share', body: 'Open Publish in Snag Builder, sync to GitHub, then deploy with Vercel.' },
    ],
    tips: ['Included on the free Basic plan with website/credit limits.', 'Uses AI credits (or your OpenAI key in Settings).'],
    related: ['publish-vercel', 'snagcart-modules', 'websites'],
  },
  {
    id: 'publish-vercel',
    category: 'build',
    title: 'Publish App — Deploy with Vercel',
    summary: 'Push a Snag Builder app to GitHub and deploy it live on Vercel. Covers token scope, GitHub Login Connection, and Create vs Connect project.',
    path: '/snagbuilder',
    pathLabel: 'Open Snag Builder',
    routeMatch: ['/snagbuilder', '/alifullstack'],
    keywords: [
      'publish',
      'vercel',
      'deploy',
      'github',
      'token',
      'full account',
      'login connection',
      'create project',
      'snag builder',
    ],
    steps: [
      {
        title: 'Open Publish',
        body: 'In Snag Builder, open an app so the preview panel is visible. Use the tabs at the top of the preview (Preview · Code · Configure · Publish) and click Publish.',
      },
      {
        title: 'Connect & sync GitHub first',
        body: 'Under GitHub, connect your account (device login), then Create new repo or Connect existing. Click Sync to GitHub and confirm the code appears on github.com. Vercel create/deploy needs a linked GitHub repo.',
      },
      {
        title: 'Create a Vercel API token (scope matters)',
        body: 'Go to vercel.com/account/settings/tokens → Create Token. Scope must be Full Account, or a specific team where your role is Owner (not Viewer / Developer-only). Project-scoped or read-only tokens can show Connected in Settings but fail Create Project with 403.',
      },
      {
        title: 'Paste the token in Snag Builder',
        body: 'Settings → Vercel → paste the token → Connect. To rotate a broken token, use Replace token (or Disconnect — the paste box returns). Connected only means the token can read your account; create permission is separate.',
      },
      {
        title: 'Link GitHub on your Vercel account (Login Connection)',
        body: 'Create Project imports the GitHub repo. Your Vercel account must have GitHub under Login Connections / Authentication at vercel.com/account. Also install/authorize the Vercel GitHub App for that repo (or org). Without this you get: “You need to add a Login Connection to your GitHub account first.”',
      },
      {
        title: 'Create project or connect existing',
        body: 'On Publish → Vercel: Create new project (names the Vercel project and links the synced repo), or Connect to existing project if you already imported the repo in the Vercel dashboard. Then open the deployment URL when the build finishes.',
      },
      {
        title: 'If Create Project still fails',
        body: 'In Vercel: Add New Project → import the same GitHub repo → deploy there. Back in Snag Builder Publish, use Connect to existing project and pick it. Refresh Deployments to confirm builds.',
      },
    ],
    tips: [
      'Snag Builder GitHub connect ≠ Vercel GitHub Login Connection — both are required for Create Project.',
      '403 on create → new Full Account / Owner-team token. 400 Login Connection → connect GitHub on vercel.com/account.',
      'Use Replace token after changing scopes so Settings does not keep a stale token.',
    ],
    related: ['app-builder', 'websites', 'api-setup'],
  },
  {
    id: 'ai-tools',
    category: 'ai',
    title: 'AI Tools',
    summary: 'Hub for 15+ AI utilities — writing, code, images, voice, and research.',
    path: '/ai-tools-dashboard',
    pathLabel: 'AI Tools',
    routeMatch: ['/ai-tools-dashboard', '/ai-tool/', '/ai-tools/'],
    keywords: ['ai writer', 'chat', 'code', 'image', 'voice', 'plagiarism', 'detector'],
    steps: [
      { title: 'Open the hub', body: 'AI Tools dashboard lists every tool as a card — click to open.' },
      { title: 'AI Writer & Article Wizard', body: 'Long-form blogs and structured articles for your site or campaigns.' },
      { title: 'AI Chat & File Chat', body: 'General assistant or upload PDFs/docs to ask questions.' },
      { title: 'AI Code', body: 'Generate snippets, explain errors, or scaffold components.' },
      { title: 'Image & voice tools', body: 'Chat Image, Product Photo, Voiceover, Speech-to-Text for rich media.' },
      { title: 'Premium add-ons', body: 'Plagiarism Checker, AI Detector, and YouTube are separate add-ons. Voice is covered by Voice Campaigns.' },
    ],
    tips: ['Growth plan includes the full AI Tools set.', 'Output can be copied directly into the website editor or email campaigns.'],
    related: ['media-studio', 'ai-studio', 'marketing-email'],
  },
  {
    id: 'media-studio',
    category: 'ai',
    title: 'Media Studio',
    summary: 'Generate images, video assets, and creative content for sites and campaigns.',
    path: '/media-studio',
    pathLabel: 'Media Studio',
    routeMatch: ['/media-studio', '/ai-presentation', '/marketing-suite/ads'],
    keywords: ['media', 'image', 'video', 'creative', 'presentation', 'social'],
    steps: [
      { title: 'Create visuals', body: 'Generate brand-aligned images for hero sections, products, and ads.' },
      { title: 'AI Presentation', body: 'Build slide decks from a topic outline — export or present.' },
      { title: 'AI Ads (Social)', body: 'Draft, schedule, and queue posts from Marketing Suite → AI Ads.' },
    ],
    tips: ['Available on Starter plan and above.', 'Download assets and upload them in the website editor.'],
    related: ['ai-tools', 'websites'],
  },
  {
    id: 'ai-studio',
    category: 'ai',
    title: 'AI Studio',
    summary: 'Build custom AI projects, agents, and automations beyond preset tools.',
    path: '/ai-studio',
    pathLabel: 'AI Studio',
    routeMatch: ['/ai-studio', '/ai-builder/', '/create-ai-project'],
    keywords: ['ai studio', 'agent', 'project', 'custom ai', 'builder'],
    steps: [
      { title: 'Create a project', body: 'Start a new AI project with instructions, knowledge files, and tools.' },
      { title: 'Chat & iterate', body: 'Test your agent in the project workspace and refine prompts.' },
      { title: 'Connect to workflows', body: 'Use with Automations or embed logic in your sites via API.' },
    ],
    tips: ['Basic plan includes limited AI Studio runs; higher tiers increase limits.'],
    related: ['automations', 'connections', 'ai-cofounder'],
  },
  {
    id: 'ai-cofounder',
    category: 'ai',
    title: 'AI Co-founder',
    summary: 'Autonomous company OS — plans a roadmap, runs real CRM / email / Builder / SEO / social / support work, asks you before send or publish, and keeps a session Outputs hub.',
    path: '/ai-cofounder',
    pathLabel: 'AI Co-founder',
    routeMatch: ['/ai-cofounder'],
    keywords: [
      'ai cofounder', 'co-founder', 'polsia', 'autonomous', 'roadmap', 'approve',
      'facebook', 'social post', 'outputs hub', 'session', 'company os',
    ],
    steps: [
      {
        title: '1. Understand the model',
        body: 'The co-founder works inside your Snagcart account. Drafts and CRM saves do not need Facebook/email APIs. Live send/publish uses YOUR keys — we never post from a shared Snagcart Facebook page.',
      },
      {
        title: '2. Connect what you need (optional first)',
        body: 'Recommended: Settings → API Keys → OPENAI_API_KEY for smarter plans. For live email: SendGrid/SMTP (or Connections → Gmail). For Facebook/Instagram/LinkedIn/X: Settings → API Keys → Social Media Publishing (FACEBOOK_PAGE_ID + PAGE_ACCESS_TOKEN, etc.).',
      },
      {
        title: '3. Create your company',
        body: 'Open AI Co-founder → enter name, what it does, goals, and audience → Create my company.',
      },
      {
        title: '4. Launch',
        body: 'Click Launch co-founder. It generates ~10–14 roadmap tasks and starts executing safe ones (seed CRM leads, draft emails, create Builder projects, draft social posts, start SEO runs). Default cap is about 40 completed tasks per day.',
      },
      {
        title: '5. Approve risky actions',
        body: 'Pending approvals appear for send campaign, schedule/publish social, SEO apply, support replies, and deploy. Approve only when credentials are ready — or Reject and keep drafts.',
      },
      {
        title: '6. See all outputs',
        body: 'Click Outputs hub (or Sessions → Open outputs). One page lists CRM leads, campaigns, websites, SEO, social, ads, and support drafts — each with an Open link into the real module.',
      },
      {
        title: '7. Steer with chat',
        body: 'Use Strategist chat: e.g. “focus on Austin coffee shops” or “approve”. Pause saves the session; New session archives the run and starts fresh.',
      },
    ],
    tips: [
      'You can launch with zero social keys — you will still get drafts in Outputs hub; connect APIs before Approve → publish.',
      'Facebook needs FACEBOOK_PAGE_ID + FACEBOOK_PAGE_ACCESS_TOKEN under Settings → API Keys.',
      'Open Platform Guide → AI Co-founder anytime from the ? widget on this page.',
      'Dashboard button returns to Platform Overview (/dashboard).',
    ],
    related: ['api-setup', 'connections', 'marketing-suite', 'marketing-email', 'snag-builder', 'media-studio'],
  },
  {
    id: 'marketing-suite',
    category: 'grow',
    title: 'Marketing Suite',
    summary: 'Email, SMS, WhatsApp, and CRM — run campaigns and manage contacts.',
    path: '/marketing-suite',
    pathLabel: 'Marketing Suite',
    routeMatch: ['/marketing-suite', '/marketing/'],
    keywords: ['marketing', 'email', 'sms', 'whatsapp', 'crm', 'campaign'],
    steps: [
      { title: 'Hub overview', body: 'Marketing Suite dashboard links to Email, SMS, WhatsApp, and CRM sub-modules.' },
      { title: 'Email Marketing', body: 'Lists, templates, campaigns, and delivery analytics.' },
      { title: 'SMS Marketing', body: 'Bulk SMS with contact groups and delivery reports.' },
      { title: 'WhatsApp Marketing', body: 'Broadcasts and chatbots (Growth plan).' },
      { title: 'Marketing CRM', body: 'Contacts, pipelines, and activity tied to campaigns.' },
    ],
    tips: ['Import CSV contacts or capture leads from your website forms.', 'Connect Gmail/SMTP in Connections for sending.', 'AI Co-founder can create draft campaigns here automatically.'],
    related: ['automations', 'lead-scraper', 'connections', 'ai-cofounder'],
  },
  {
    id: 'marketing-email',
    category: 'grow',
    title: 'Email Marketing',
    summary: 'Lists, templates, campaigns, and analytics.',
    path: '/marketing-suite/email',
    pathLabel: 'Email Marketing',
    routeMatch: ['/marketing-suite/email'],
    keywords: ['email', 'newsletter', 'campaign', 'template', 'smtp'],
    steps: [
      { title: 'Create a list', body: 'Add contacts manually or import CSV.' },
      { title: 'Design a campaign', body: 'Pick a template, write subject and body, personalize merge tags.' },
      { title: 'Test & send', body: 'Send a test to yourself, then schedule or send immediately.' },
      { title: 'Track results', body: 'Monitor opens, clicks, bounces, and unsubscribes.' },
    ],
    tips: ['Basic plan includes 100 sends/month; Starter 2,000; Growth 10,000.'],
    related: ['marketing-suite', 'automations'],
  },
  {
    id: 'lead-scraper',
    category: 'grow',
    title: 'Lead Scraper',
    summary: 'Find business leads by niche and location, then export or push to CRM.',
    path: '/lead-scraper',
    pathLabel: 'Lead Scraper',
    routeMatch: ['/lead-scraper'],
    keywords: ['lead', 'scraper', 'prospect', 'export', 'google maps'],
    steps: [
      { title: 'Define search', body: 'Enter business type, city, or region to find prospects.' },
      { title: 'Review results', body: 'Filter by rating, website presence, or contact info.' },
      { title: 'Export or sync', body: 'Download CSV or send to Marketing CRM for follow-up.' },
    ],
    tips: ['Combine with Automations to auto-email new scraped leads.', 'Monthly limits depend on your plan tier.'],
    related: ['marketing-suite', 'automations', 'bot-leads'],
  },
  {
    id: 'automations',
    category: 'operate',
    title: 'Automations',
    summary: 'Workflow triggers across email, SMS, webhooks, and internal events.',
    path: '/automations',
    pathLabel: 'Automations',
    routeMatch: ['/automations'],
    keywords: ['automation', 'workflow', 'trigger', 'webhook', 'zap'],
    steps: [
      { title: 'Create a workflow', body: 'Choose a trigger: form submit, new lead, schedule, or webhook.' },
      { title: 'Add actions', body: 'Send email/SMS, update CRM, call an API, or notify your team.' },
      { title: 'Test & enable', body: 'Run a test payload, then turn the workflow on.' },
    ],
    tips: ['Starter plan unlocks Automations.', 'Pair with Connections for third-party app triggers.'],
    related: ['connections', 'marketing-suite'],
  },
  {
    id: 'api-setup',
    category: 'integrations',
    title: 'API Keys & Integrations Setup',
    summary: 'Configure external services in Settings → API Keys. One set of credentials powers all modules on your account.',
    path: SETTINGS_API_KEYS_PATH,
    pathLabel: 'Open Settings → API Keys',
    routeMatch: ['/settings', '/platform-guide'],
    keywords: ['api', 'api key', 'openai', 'stripe', 'twilio', 'sendgrid', 'smtp', 'google maps', 'integration', 'credentials', 'settings', 'webhook', 'oauth'],
    steps: [
      { title: 'Open Settings → API Keys', body: 'Sidebar → Settings → API Keys tab. All platform credentials are stored per-user in platform_settings.' },
      { title: 'Save & test', body: 'Enter values, click Save Changes, then use Test connection on supported providers (SendGrid, Twilio, NameSilo, etc.).' },
      { title: 'Environment vs user keys', body: 'Keys you save show "User configured". Server .env keys show "Environment" — user keys override for your account.' },
      { title: 'Connections vs API Keys', body: 'OAuth apps (Google login, Supabase) use Connections (/connections). API Keys are for secret tokens pasted directly.' },
      { title: 'Module readiness', body: 'If a module fails with "not configured", use the knowledge base below to see which keys it needs.' },
    ],
    tips: [
      'Minimum to start: OPENAI_API_KEY for AI features.',
      'Never commit real keys to git — use Settings UI or server .env only.',
      'PhonePe + Stripe plan keys: platform admin or white-label agency under Settings → API Keys (agency keys apply to that agency’s clients).',
    ],
    related: ['connections', 'settings', 'marketing-email', 'lead-scraper', 'ai-cofounder'],
    showApiKnowledgeBase: true,
  },
  {
    id: 'connections',
    category: 'operate',
    title: 'Connections',
    summary: 'OAuth integrations — Google, Stripe, Supabase, webhooks, and more.',
    path: '/connections',
    pathLabel: 'Connections',
    routeMatch: ['/connections'],
    keywords: ['connect', 'integration', 'oauth', 'api', 'stripe', 'google'],
    steps: [
      { title: 'Browse integrations', body: 'See available apps and connection status.' },
      { title: 'Authorize', body: 'Click Connect and complete OAuth or paste API keys where required.' },
      { title: 'Use in modules', body: 'Connected services power email sending, payments, and AI tools.' },
    ],
    tips: [
      'Supabase connections wire database + auth into Snag Builder projects.',
      'Connect Gmail here so AI Co-founder can scan inbox / send support replies.',
      'Revoke access anytime from Connections.',
    ],
    related: ['api-setup', 'automations', 'app-builder', 'ai-cofounder'],
  },
  {
    id: 'bot-leads',
    category: 'grow',
    title: 'Bot Leads & Calls',
    summary: 'AI bots for inbound leads, chat, and call handling (premium add-on).',
    path: '/bot-leads',
    pathLabel: 'Bot Leads',
    routeMatch: ['/bot-leads'],
    keywords: ['bot', 'leads', 'calls', 'chatbot', 'inbound', 'twilio'],
    steps: [
      { title: 'Purchase add-on', body: 'Buy Bot Leads & Calls from Snagcart Modules if not on your plan.' },
      { title: 'Configure bot', body: 'Set greeting, FAQs, and handoff rules for web chat or phone.' },
      { title: 'Capture leads', body: 'Leads flow to CRM or trigger automations.' },
    ],
    tips: ['Works alongside website Live Chat site module on Growth.'],
    related: ['snagcart-modules', 'voice-campaigns'],
  },
  {
    id: 'voice-campaigns',
    category: 'grow',
    title: 'Voice Campaigns',
    summary: 'Outbound voice campaigns with Twilio (premium add-on).',
    path: '/voice-campaigns',
    pathLabel: 'Voice Campaigns',
    routeMatch: ['/voice-campaigns'],
    keywords: ['voice', 'call', 'campaign', 'twilio', 'outbound', 'phone'],
    steps: [
      { title: 'Buy add-on', body: 'Enable Voice Campaigns from Snagcart Modules.' },
      { title: 'Upload contacts', body: 'Import phone numbers with consent compliance.' },
      { title: 'Record message', body: 'TTS or uploaded audio for the campaign script.' },
      { title: 'Launch & monitor', body: 'Schedule calls and review completion stats.' },
    ],
    tips: ['Ensure TCPA/local telemarketing compliance before dialing.'],
    related: ['bot-leads', 'marketing-suite'],
  },
  {
    id: 'ecommerce',
    category: 'operate',
    title: 'Operator & Commerce',
    summary: 'Orders, products, checkout, and operator cockpit for store sites.',
    path: '/dashboard',
    pathLabel: 'My Websites',
    routeMatch: ['/ecommerce/', '/sites/', '/store/', '/operator'],
    keywords: ['ecommerce', 'shop', 'orders', 'products', 'checkout', 'operator'],
    steps: [
      { title: 'Enable on site', body: 'Turn on eCommerce site module when editing a website.' },
      { title: 'Add products', body: 'Open eCommerce panel for that site — products, variants, pricing.' },
      { title: 'Operator cockpit', body: 'Manage live orders, fulfillment, and delivery from Operator view.' },
      { title: 'Customer checkout', body: 'Customers use /store/{siteId}/checkout on your published domain.' },
    ],
    tips: ['Commerce+ (multi-currency, delivery, Shopify/Woo) is included on all plans.'],
    related: ['websites', 'snagcart-modules'],
  },
  {
    id: 'subscription-plans',
    category: 'account',
    title: 'Choose Plan',
    summary: 'Basic (free), Starter, and Growth — compare modules and upgrade.',
    path: '/subscription-plans',
    pathLabel: 'Choose Plan',
    routeMatch: ['/subscription-plans'],
    keywords: ['plan', 'subscription', 'pricing', 'upgrade', 'basic', 'starter', 'growth'],
    steps: [
      { title: 'Compare tiers', body: 'Each card shows highlights and a full module breakdown.' },
      { title: 'Activate Basic', body: 'Free tier activates instantly — no payment.' },
      { title: 'Pay for Starter/Growth', body: 'Select plan → choose region → Stripe (card) or PhonePe (India).' },
      { title: 'Mock billing (dev)', body: 'In local dev, confirm mock payment to activate without real charges.' },
    ],
    tips: ['Your active plan shows as a badge on the plans page.', 'Downgrades contact support — data is retained.'],
    related: ['snagcart-modules'],
  },
  {
    id: 'snagcart-modules',
    category: 'account',
    title: 'Snagcart Modules',
    summary: 'See activated modules and buy premium add-ons à la carte.',
    path: '/snagcart/apps',
    pathLabel: 'Snagcart Modules',
    routeMatch: ['/snagcart/apps', '/snagcart'],
    keywords: ['modules', 'addon', 'add-on', 'activated', 'premium', 'unlock'],
    steps: [
      { title: 'Activated modules', body: 'Grouped list of everything your plan + purchased add-ons unlock.' },
      { title: 'Buy add-ons', body: 'Optional extra AI tools (Plagiarism, Detector, YouTube) have individual prices. Voice is Voice Campaigns only.' },
      { title: 'Payment flow', body: 'Same checkout as plans — Stripe or PhonePe, then module activates immediately.' },
    ],
    tips: ['Module count in the header updates after each purchase.', 'Growth includes almost all platform modules except add-ons.'],
    related: ['subscription-plans', 'app-builder'],
  },
  {
    id: 'support',
    category: 'account',
    title: 'Support',
    summary: 'Submit tickets and track responses from the Snagcart team.',
    path: '/support',
    pathLabel: 'Support',
    routeMatch: ['/support'],
    keywords: ['support', 'help', 'ticket', 'contact', 'issue'],
    steps: [
      { title: 'Open a ticket', body: 'Describe your issue with steps to reproduce.' },
      { title: 'Track status', body: 'See open, in-progress, and resolved tickets in the list.' },
    ],
    tips: ['For how-to questions, Platform Guide and the ? widget are faster than tickets.'],
    related: ['welcome'],
  },
  {
    id: 'settings',
    category: 'account',
    title: 'Settings',
    summary: 'Profile, password, API keys, billing info, and whitelabel options.',
    path: '/settings',
    pathLabel: 'Settings',
    routeMatch: ['/settings'],
    keywords: ['settings', 'profile', 'password', 'api key', 'account'],
    steps: [
      { title: 'Profile', body: 'Update name, email display, and avatar.' },
      { title: 'Security', body: 'Change password and manage sessions.' },
      { title: 'Integrations', body: 'Platform-level API keys used across modules.' },
    ],
    tips: ['Agency Whitelabel add-on enables custom brand and client resale from Settings.'],
    related: ['api-setup', 'snagcart-modules'],
  },
];

const normalize = (s) => (s || '').toLowerCase().trim();

export function searchTutorial(query) {
  const q = normalize(query);
  if (!q) return TUTORIAL_SECTIONS;
  return TUTORIAL_SECTIONS.filter((section) => {
    const apiHay = section.showApiKnowledgeBase
      ? API_SETUP_BLOCKS.flatMap((b) => [b.label, ...b.modules, ...b.keys.map((k) => `${k.key} ${k.label} ${k.usedFor || ''}`)]).join(' ')
      : '';
    const haystack = [
      section.title,
      section.summary,
      ...(section.keywords || []),
      ...(section.steps || []).map((s) => `${s.title} ${s.body}`),
      apiHay,
    ].join(' ').toLowerCase();
    return haystack.includes(q);
  });
}

export function getSectionById(id) {
  return TUTORIAL_SECTIONS.find((s) => s.id === id) || null;
}

export function getSectionsByCategory(categoryId) {
  return TUTORIAL_SECTIONS.filter((s) => s.category === categoryId);
}

export function getContextSection(pathname) {
  const path = pathname || '';
  if (path.startsWith('/settings')) {
    return getSectionById('api-setup') || getSectionById('settings');
  }
  let best = null;
  let bestLen = 0;
  for (const section of TUTORIAL_SECTIONS) {
    for (const match of section.routeMatch || []) {
      if (path === match || (match.endsWith('/') && path.startsWith(match)) || (!match.endsWith('/') && path.startsWith(match) && match.length > 1)) {
        if (match.length > bestLen) {
          best = section;
          bestLen = match.length;
        }
      }
    }
  }
  return best || getSectionById('welcome');
}

export function searchWorkflows(query) {
  const q = normalize(query);
  if (!q) return QUICK_WORKFLOWS;
  return QUICK_WORKFLOWS.filter((w) => {
    const hay = [w.title, ...(w.steps || [])].join(' ').toLowerCase();
    return hay.includes(q);
  });
}

export const WELCOME_TOUR_SLIDES = [
  {
    title: 'Welcome to Snagcart',
    body: 'Build websites, run marketing campaigns, and automate growth — all from one dashboard.',
    cta: 'Next',
  },
  {
    title: 'Start with a plan',
    body: 'Basic is free forever with every dashboard module. Upgrade to Starter or Growth for more sites, credits, and caps. Optional extra AI tools are add-ons.',
    cta: 'Next',
    path: '/subscription-plans',
    pathLabel: 'View plans',
  },
  {
    title: 'Create your first site',
    body: 'Open My Websites → pick a template or use AI → edit → publish. You will get a live URL in minutes.',
    cta: 'Next',
    path: '/dashboard',
    pathLabel: 'Go to websites',
  },
  {
    title: 'Explore the platform',
    body: 'AI Tools, Marketing Suite, Lead Scraper, Automations, and Snag Builder live in the sidebar. Each module has its own workspace.',
    cta: 'Next',
  },
  {
    title: 'Help is always nearby',
    body: 'Click the ? button for contextual tips. Platform Guide covers every module plus a full API Keys knowledge base (OpenAI, Stripe, Twilio, email, domains, and more).',
    cta: 'Open Platform Guide',
    path: '/platform-guide?section=api-setup',
    pathLabel: 'API setup guide',
  },
];

export const GUIDE_STORAGE_KEY = 'snagcart_platform_guide_seen_v1';
